import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemTabs } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { useAuth } from "../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { localStorageService } from "../../services/LocalStorageService";

/** The Schedule Template layouts. Values double as their i18n label keys. */
export enum ScheduleTemplatePageTab {
	Weekly = "weekly.schedule.template",
	Consecutive = "consecutive.schedule.template",
	Coverage = "coverage.schedule.template",
}

/**
 * The strip's last tab. It is a page of its own (`/locations`), not a layout of the template, so it is not a
 * `ScheduleTemplatePageTab`: never persisted, and its value is also the tab's test-id suffix.
 */
export const SCHEDULE_TEMPLATE_LOCATIONS_TAB = "locations";

/** What the strip can show as selected: a template layout, or the Locations page. */
export type ScheduleTemplateSectionTab = ScheduleTemplatePageTab | typeof SCHEDULE_TEMPLATE_LOCATIONS_TAB;

const isScheduleTemplateTab = (value: string | null): value is ScheduleTemplatePageTab =>
	value === ScheduleTemplatePageTab.Weekly || value === ScheduleTemplatePageTab.Consecutive || value === ScheduleTemplatePageTab.Coverage;

/**
 * Owns the selected-tab state: persisted per-user in localStorage and gated by feature flags —
 * Consecutive by `ConsecutiveMasterSchedule`, Coverage by `CoveragePeopleTabs` (People has moved to the
 * Staffing area, so this flag now gates Coverage alone). If the persisted tab requires a flag that's off
 * it falls back to Weekly — both on initial load and if the flag flips off at runtime.
 */
export const useScheduleTemplateTab = () => {
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const isConsecutiveEnabled = useFeatureFlag(FEATURE_FLAGS.ConsecutiveMasterSchedule);
	const isCoverageEnabled = useFeatureFlag(FEATURE_FLAGS.CoveragePeopleTabs);

	const [selectedTab, setSelectedTabState] = useState<ScheduleTemplatePageTab>(() => {
		const persisted = userId ? localStorageService.getScheduleTemplateTab(userId) : null;
		if (!isScheduleTemplateTab(persisted)) return ScheduleTemplatePageTab.Weekly;
		if (persisted === ScheduleTemplatePageTab.Consecutive && isConsecutiveEnabled) return ScheduleTemplatePageTab.Consecutive;
		if (persisted === ScheduleTemplatePageTab.Coverage && isCoverageEnabled) return ScheduleTemplatePageTab.Coverage;
		return ScheduleTemplatePageTab.Weekly;
	});

	// Force back to weekly if the selected tab's flag becomes unavailable (turned off while selected).
	useEffect(() => {
		if (selectedTab === ScheduleTemplatePageTab.Consecutive && !isConsecutiveEnabled) setSelectedTabState(ScheduleTemplatePageTab.Weekly);
		if (selectedTab === ScheduleTemplatePageTab.Coverage && !isCoverageEnabled) setSelectedTabState(ScheduleTemplatePageTab.Weekly);
	}, [isConsecutiveEnabled, isCoverageEnabled, selectedTab]);

	const setSelectedTab = useCallback(
		(tab: ScheduleTemplatePageTab) => {
			setSelectedTabState(tab);
			if (userId) localStorageService.setScheduleTemplateTab(tab, userId);
		},
		[userId],
	);

	return { selectedTab, setSelectedTab, isConsecutiveEnabled, isCoverageEnabled };
};

type Props = {
	selectedTab: ScheduleTemplateSectionTab;
	/** A template layout was chosen. */
	onChange: (tab: ScheduleTemplatePageTab) => void;
	/** Locations was chosen: the Schedule Template navigates to the page; the page itself has nothing to do. */
	onOpenLocations: () => void;
	isConsecutiveEnabled: boolean;
	isCoverageEnabled: boolean;
};

/**
 * Weekly / Consecutive / Coverage / Locations tab strip, shared by the Schedule Template (injected into each view's
 * header as its `trailing` slot) and the Locations page. Weekly is always present; Consecutive appears with its flag;
 * Coverage is gated by `CoveragePeopleTabs` (People now lives in the Staffing area); Locations is always last — it
 * leaves for another page, so it reads as the trailing section rather than a fourth layout.
 */
export const ScheduleTemplateTabs: FC<Props> = ({ selectedTab, onChange, onOpenLocations, isConsecutiveEnabled, isCoverageEnabled }) => {
	const { t } = useTranslation();

	const tabs: SystemTab<ScheduleTemplateSectionTab>[] = [{ label: t(ScheduleTemplatePageTab.Weekly), value: ScheduleTemplatePageTab.Weekly }];
	if (isConsecutiveEnabled) tabs.push({ label: t(ScheduleTemplatePageTab.Consecutive), value: ScheduleTemplatePageTab.Consecutive });
	if (isCoverageEnabled) tabs.push({ label: t(ScheduleTemplatePageTab.Coverage), value: ScheduleTemplatePageTab.Coverage });
	tabs.push({ label: t("locations.entry.label"), value: SCHEDULE_TEMPLATE_LOCATIONS_TAB, icon: <PlaceOutlinedIcon fontSize="small" /> });

	const handleChange = (tab: ScheduleTemplateSectionTab) => {
		if (tab === SCHEDULE_TEMPLATE_LOCATIONS_TAB) onOpenLocations();
		else onChange(tab);
	};

	return (
		<SystemTabs
			tabs={tabs}
			currentTab={selectedTab}
			onTabChange={handleChange}
			dataTestId="schedule-template-tabs"
			// The walkthrough's "these switch the layout" step anchors here only when there is a layout to switch to, as
			// before Locations joined the strip; with Weekly alone the attribute is left off and the step is skipped.
			data-annie-target={isConsecutiveEnabled || isCoverageEnabled ? "master-schedule-layout-tabs" : undefined}
		/>
	);
};
